import type Owner from '@ember/owner';
import Component from '@glimmer/component';

import type { SourceInfo } from 'webapp-types';

import ToolTipComponent from 'webapp/components/tool-tip';

export interface UsedSourcesListSignature {
	Element: HTMLDivElement;
	Args: {
		sources: SourceInfo[];
		maxShownSourcesCount?: number;
		size?: 'sm';
	};
}

export default class UsedSourcesListComponent extends Component<UsedSourcesListSignature> {
	maxShownSourcesCount: number;

	constructor(owner: Owner, args: UsedSourcesListSignature['Args']) {
		super(owner, args);

		this.maxShownSourcesCount = this.args.maxShownSourcesCount ?? 4;
	}

	get className() {
		const className = ['used-sources-list'];
		const size = this.args.size;

		if (size) {
			className.push(`used-sources-list--${size}`);
		}

		return className.join(' ');
	}

	get sources(): SourceInfo[] {
		return this.args.sources || [];
	}

	get shownSourcesCount(): number {
		const sources = this.sources,
			maxShownSourcesCount = this.maxShownSourcesCount;

		return sources.length > maxShownSourcesCount
			? maxShownSourcesCount - 1
			: Math.min(sources.length, maxShownSourcesCount);
	}

	get shownSources(): SourceInfo[] {
		const sources = this.sources;
		const uniqueMap = new Map<string, SourceInfo>();
		const shownSources: SourceInfo[] = [];

		if (sources?.length) {
			for (const source of sources) {
				const key = source.label;
				if (!uniqueMap.has(key)) {
					uniqueMap.set(key, source);
					shownSources.push(source);
					if (shownSources.length >= this.shownSourcesCount) {
						break;
					}
				}
			}
		}

		return shownSources;
	}

	get hiddenSources(): SourceInfo[] {
		const sources = this.sources;
		let hiddenSources: SourceInfo[] = [];

		if (sources?.length) {
			hiddenSources = sources.slice(this.shownSourcesCount, sources.length);
		}

		return hiddenSources;
	}

	get hiddenSourcesCount(): number {
		return this.hiddenSources.length;
	}

	get hiddenSourcesText(): string {
		return this.hiddenSources.map((s) => s.label).join(', ');
	}

	<template>
		<div class={{this.className}}>
			<ul>
				{{#each this.shownSources as |source|}}
					<li>
						<img src={{source.avatar}} alt={{source.label}} />
						<ToolTipComponent @text={{source.label}} />
					</li>
				{{/each}}

				{{#if this.hiddenSourcesCount}}
					<li>
						<span class="hidden">
							+{{this.hiddenSourcesCount}}
							<ToolTipComponent @text={{this.hiddenSourcesText}} />
						</span>
					</li>
				{{/if}}
			</ul>
		</div>
	</template>
}
